import {
  InteractionTag,
  InteractionTagPrimary,
  Menu,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Tag,
} from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { HEADER_0 } from '../../constants/files'
import { useFilePreviewDialog } from '../../hooks/useFilePreviewDialog'
import type { MessageSource } from '../../types'
import { getFileExtensionFromName } from '../../utils/files'
import { CONTEXT_ENTRY_ICON_SIZE } from '../ContextCard/context-entry-styles'
import { FileIcon } from '../FileIcon/FileIcon'
import { useMessageSourcesStyles } from './MessageSources.styles'
import { useSourcesPopover } from './useSourcesPopover'

interface MessageSourcesProps {
  /** Undefined means the sources couldn't be loaded. */
  sources?: MessageSource[]
}

const MessageSources = ({ sources }: MessageSourcesProps) => {
  const { t } = useTranslation()
  const loadFailed = sources === undefined
  const styles = useMessageSourcesStyles()
  const { openFilePreviewDialog } = useFilePreviewDialog()
  const { menuProps, triggerProps, popoverProps } = useSourcesPopover()

  return (
    <>
      <span aria-hidden className={styles.separator}>
        ·
      </span>
      <Menu {...menuProps}>
        {/* Trigger wraps the primary button (not the tag wrapper) so aria-expanded and focus land on it */}
        <InteractionTag size="small" shape="circular" appearance="filled">
          <MenuTrigger disableButtonEnhancement>
            <InteractionTagPrimary className={styles.tagPrimary} {...triggerProps}>
              {t('chatBubble.sources')}
            </InteractionTagPrimary>
          </MenuTrigger>
        </InteractionTag>
        <MenuPopover
          className={mergeClasses(styles.popover, loadFailed && styles.popoverError)}
          {...popoverProps}>
          <MenuList>
            {loadFailed ? (
              <MenuItem
                disabled
                className={styles.errorItem}
                content={{ className: styles.errorItemContent }}>
                {t('chatBubble.sourcesLoadFailed')}
              </MenuItem>
            ) : (
              sources.map((source) => {
                // Sectioned sources are previewed through their chips, so the file row is only a label
                const hasSections = source.sections.length > 0
                return (
                  <MenuGroup key={source.fileId} aria-label={source.fileName}>
                    {hasSections ? (
                      <MenuGroupHeader className={styles.fileHeader}>
                        <FileIcon
                          fileType={getFileExtensionFromName(source.fileName)}
                          width={CONTEXT_ENTRY_ICON_SIZE}
                          height={CONTEXT_ENTRY_ICON_SIZE}
                        />
                        <span className={styles.fileHeaderText}>{source.fileName}</span>
                      </MenuGroupHeader>
                    ) : (
                      <MenuItem
                        className={styles.fileItem}
                        icon={{
                          className: styles.fileItemIcon,
                          children: (
                            <FileIcon
                              fileType={getFileExtensionFromName(source.fileName)}
                              width={CONTEXT_ENTRY_ICON_SIZE}
                              height={CONTEXT_ENTRY_ICON_SIZE}
                            />
                          ),
                        }}
                        content={{ className: styles.fileItemContent }}
                        aria-label={t('files.actions.previewFile', { fileName: source.fileName })}
                        onClick={() =>
                          openFilePreviewDialog({
                            fileId: source.fileId,
                            fileName: source.fileName,
                          })
                        }>
                        {source.fileName}
                      </MenuItem>
                    )}
                    {source.sections.map((section) => {
                      const sectionTitle =
                        section.title === HEADER_0 ? t('files.headerZeroLabel') : section.title
                      return (
                        <MenuItem
                          key={section.id}
                          className={styles.sectionItem}
                          content={{ className: styles.sectionItemContent }}
                          aria-label={t('files.actions.previewSection', { sectionTitle })}
                          onClick={() =>
                            openFilePreviewDialog({
                              fileId: source.fileId,
                              fileName: source.fileName,
                              sectionId: section.id,
                              sectionTitle,
                            })
                          }>
                          <Tag
                            size="extra-small"
                            shape="circular"
                            appearance="filled"
                            className={styles.sectionTag}
                            primaryText={{ className: styles.sectionTagText }}>
                            {sectionTitle}
                          </Tag>
                        </MenuItem>
                      )
                    })}
                  </MenuGroup>
                )
              })
            )}
          </MenuList>
        </MenuPopover>
      </Menu>
    </>
  )
}

export { MessageSources }
